/**
 * TelePortal (Mobility) track visual config — the single source of truth for
 * the three relocation tracks' identity + brand accents, shared by the citizen
 * TelePortal page and the business Sponsored-modal Mobility tab.
 *
 * PRD §8.4 track colors:
 *   Legal  = Indigo #6366F1
 *   Travel = Amber  #F59E0B
 *   Setup  = Teal   #14B8A6
 *
 * Framework-light on purpose: it carries i18n KEY paths and Tailwind CLASS
 * strings (resolved by each app), and a hex accent for SVG/inline styles. It
 * does NOT carry an icon component — each app maps `id → icon` locally so this
 * package stays free of `@tonnie/assets` / React deps.
 */

export type TeleportalTrackId = 'legal' | 'travel' | 'setup';

export interface TeleportalTrackVisual {
  id: TeleportalTrackId;
  /** i18n key for the track display name (e.g. teleportal.trackLegal). */
  nameKey: string;
  /** i18n key for a short description. */
  descriptionKey: string;
  /** Text color class (light + dark). */
  color: string;
  /** Surface background class. */
  bgColor: string;
  /** Border class. */
  borderColor: string;
  /** Solid accent background (icon chips) with the text / icon colour that sits on it. */
  accentTw: string;
  /** Left-edge accent of a track card (`border-l-4` + this). */
  edgeColor: string;
  /** Progress-bar track class. */
  progressBg: string;
  /** Progress-bar fill class. */
  progressFill: string;
}

export const TELEPORTAL_TRACK_VISUALS: readonly TeleportalTrackVisual[] = [
  {
    id: 'legal',
    nameKey: 'teleportal.trackLegal',
    descriptionKey: 'teleportal.trackLegalDescShort',
    color: 'text-tone-blue',
    bgColor: 'bg-tone-blue-subtle',
    borderColor: 'border-tone-blue-line',
    accentTw: 'bg-tone-blue-solid text-tone-blue-on-solid',
    edgeColor: 'border-l-tone-blue-solid',
    progressBg: 'bg-tone-blue-subtle',
    progressFill: 'bg-tone-blue-solid',
  },
  {
    id: 'travel',
    nameKey: 'teleportal.trackTravel',
    descriptionKey: 'teleportal.trackTravelDescShort',
    color: 'text-tone-amber',
    bgColor: 'bg-tone-amber-subtle',
    borderColor: 'border-tone-amber-line',
    accentTw: 'bg-tone-amber-solid text-tone-amber-on-solid',
    edgeColor: 'border-l-tone-amber-solid',
    progressBg: 'bg-tone-amber-subtle',
    progressFill: 'bg-tone-amber-solid',
  },
  {
    id: 'setup',
    nameKey: 'teleportal.trackSetup',
    descriptionKey: 'teleportal.trackSetupDescShort',
    color: 'text-tone-green',
    bgColor: 'bg-tone-green-subtle',
    borderColor: 'border-tone-green-line',
    accentTw: 'bg-tone-green-solid text-tone-green-on-solid',
    edgeColor: 'border-l-tone-green-solid',
    progressBg: 'bg-tone-green-subtle',
    progressFill: 'bg-tone-green-solid',
  },
] as const;

/** Lookup by track id (falls back to undefined for unknown codes). */
/** A track code from the server is one of the three tracks — or not. */
export const isTeleportalTrackId = (code: string): code is TeleportalTrackId =>
  TELEPORTAL_TRACK_VISUALS.some((track) => track.id === code);

export const TELEPORTAL_TRACK_VISUAL_BY_ID: Record<TeleportalTrackId, TeleportalTrackVisual> =
  TELEPORTAL_TRACK_VISUALS.reduce(
    (acc, cfg) => {
      acc[cfg.id] = cfg;
      return acc;
    },
    {} as Record<TeleportalTrackId, TeleportalTrackVisual>
  );
